import React, { useMemo } from "react";
import { Link as RouterLink, useLocation, useParams } from "react-router-dom";
import {
  Breadcrumbs as MuiBreadcrumbs,
  Link,
  Typography,
  Box,
} from "@mui/material";
import NavigateNextIcon from "@mui/icons-material/NavigateNext";
import HomeIcon from "@mui/icons-material/Home";

interface BreadcrumbConfig {
  [key: string]: {
    label: string;
    path?: string;
    dynamic?: boolean;
  };
}

const breadcrumbConfig: BreadcrumbConfig = {
  "/": { label: "Home", path: "/" },
  "/welcome": { label: "Welcome", path: "/welcome" },
  "/images": { label: "Images", path: "/images" },
  "/image": { label: "Image Details", dynamic: true },
  "/edit": { label: "Edit", dynamic: true },
  "/add": { label: "Add Image (OpenCV)", path: "/add" },
  "/yolo": { label: "Add Image (YOLO)", path: "/yolo" },
  "/yolocloud": { label: "Add Image (YOLO Cloud)", path: "/yolocloud" },
  "/profile": { label: "Profile", path: "/profile" },
  "/support": { label: "Support", path: "/support" },
  "/cam": { label: "Camera", path: "/cam" },
  "/privacy": { label: "Privacy Policy", path: "/privacy" },
  "/privacypolicy": { label: "Privacy Policy", path: "/privacypolicy" },
  "/playground": { label: "Playground", path: "/playground" },
};

const Breadcrumbs: React.FC = () => {
  const location = useLocation();
  const params = useParams();

  const breadcrumbs = useMemo(() => {
    const pathnames = location.pathname.split("/").filter((x) => x);
    const crumbs: Array<{ label: string; path: string; isLast: boolean }> = [];

    crumbs.push({
      label: "Home",
      path: "/",
      isLast: location.pathname === "/",
    });

    if (location.pathname === "/" || location.pathname === "/welcome") {
      return [];
    }

    // Special handling for /edit/:id routes
    if (pathnames.length === 2 && pathnames[0] === "edit") {
      const id = pathnames[1];
      crumbs.push({
        label: "Images",
        path: "/images",
        isLast: false,
      });
      crumbs.push({
        label: id,
        path: `/image/${id}`,
        isLast: false,
      });
      crumbs.push({
        label: "Edit",
        path: `/edit/${id}`,
        isLast: true,
      });
      return crumbs;
    }

    // Special handling for /image/:id routes
    if (pathnames.length === 2 && pathnames[0] === "image") {
      const id = pathnames[1];
      crumbs.push({
        label: "Images",
        path: "/images",
        isLast: false,
      });
      crumbs.push({
        label: id,
        path: `/image/${id}`,
        isLast: true,
      });
      return crumbs;
    }

    // Default handling for other routes
    let currentPath = "";
    pathnames.forEach((segment, index) => {
      currentPath += `/${segment}`;
      const isLast = index === pathnames.length - 1;
      const config = breadcrumbConfig[currentPath];

      if (config) {
        crumbs.push({
          label: config.label,
          path: currentPath,
          isLast,
        });
      } else {
        // Fallback: capitalize the segment
        crumbs.push({
          label: segment.charAt(0).toUpperCase() + segment.slice(1),
          path: currentPath,
          isLast,
        });
      }
    });

    return crumbs;
  }, [location.pathname, params.id]);

  if (breadcrumbs.length === 0) {
    return null;
  }

  return (
    <Box
      sx={{
        bgcolor: "#f5f5f5",
        px: 3,
        py: 1.5,
        borderBottom: "1px solid #e0e0e0",
      }}
    >
      <MuiBreadcrumbs
        separator={<NavigateNextIcon fontSize="small" />}
        aria-label="breadcrumb"
      >
        {breadcrumbs.map((crumb, index) => {
          if (crumb.isLast) {
            return (
              <Typography
                key={crumb.path}
                color="text.primary"
                sx={{ display: "flex", alignItems: "center" }}
              >
                {index === 0 && <HomeIcon sx={{ mr: 0.5 }} fontSize="small" />}
                {crumb.label}
              </Typography>
            );
          }

          return (
            <Link
              key={crumb.path}
              component={RouterLink}
              to={crumb.path}
              underline="hover"
              color="inherit"
              sx={{ display: "flex", alignItems: "center" }}
            >
              {index === 0 && <HomeIcon sx={{ mr: 0.5 }} fontSize="small" />}
              {crumb.label}
            </Link>
          );
        })}
      </MuiBreadcrumbs>
    </Box>
  );
};

export default Breadcrumbs;
